/** Projected dry decks. They clip the ocean sheet, not the vessel's motion. */
import { Matrix4, Vector4, type Object3D } from 'three/webgpu'
import { Discard, Fn, If, Loop, cameraPosition, float, mix, uniform, uniformArray, vec4 } from 'three/tsl'
import type { TSLNode } from './tslTypes.ts'

/** [longitudinal position, deck half-width, deck height], aft to forward. */
export type DeckStation = readonly [number, number, number]
export type HullWaterProfile = readonly DeckStation[]
export interface HullWaterSource { object: Object3D; profile: HullWaterProfile }
const MAX_STATIONS = 16
const DECK_OVERLAP = 0.08 // Start just inside the opaque deck, avoiding a seam.

function smallCraftDeck(length: number, beam: number): HullWaterProfile {
  return [[-.5,.72],[-.35,.96],[0,1],[.3,.75],[.44,.36],[.5,.035]]
    .map(([t,w]) => [t*length,w*beam/2,.8+Math.max(0,t)*.65])
}

// Deck outlines follow the authored hulls, not the larger collision bounds.
export const HULL_WATER_PROFILES: Record<string, HullWaterProfile> = {
  'container-ship': [[-200,15,12.7],[-192,23,12.7],[-176,28,12.7],[-148,29,12.7],[-85,29,12.7],[0,29,12.7],[80,29,12.7],[137,27.5,13],[163,23,13.7],[180,15.5,14.7],[191,8.5,15.6],[197,3.8,16.1],[200,.12,16.5]],
  'aircraft-carrier': [[-164,14,16],[-154,20,16],[-126,22,16],[-75,22.5,16],[-20,22.5,16],[50,22.5,16],[108,20,16.3],[139,14,17],[155,7.5,17.5],[166,.15,18]],
  zodiac: [[-2.7,.85,.35],[-1.5,.85,.35],[.7,.85,.35],[1.8,.69,.35],[2.65,.31,.35],[3,.025,.35]],
  // Stations match the shared Blender small-craft deck loft exactly.
  'police-boat': smallCraftDeck(12,4.2),
  fireboat: smallCraftDeck(16,5.2),
  sailboat: smallCraftDeck(11,3.4),
  // Raised engine deck aft, then the low cargo well running to the ramp hinge.
  'landing-craft': [[-5.3,1.38,1.15],[-2.3,1.38,1.15],[-2.15,1.2,.25],[1,1.2,.25],[4.95,1.2,.25]],
  icebreaker: [[-48,5.9,4.5],[-46,8.1,4.5],[-42,9.8,4.5],[-35,10.8,4.5],[-25,11.2,4.5],[-10,11.4,4.5],[5,11.4,4.6],[17,11.1,4.9],[26,10.25,5.4],[33,8.7,6],[39,6.4,6.6],[44,3.65,7.05],[47,1.1,7.3],[48,.06,7.35]].map(([z,w,y]) => [z,w*1.018,y]),
  'cruise-ship': [[-110,9,8],[-106,13.5,8],[-98,15.8,8],[-80,16.2,8],[-40,16.3,8],[0,16.3,8],[40,16.3,8],[70,15.8,8.3],[86,13.4,9],[98,9.2,9.7],[106,4.5,10.4],[110,.08,10.8]],
  barge: [[-110,11.8,2.4],[-100,14,2.4],[-80,14,2.4],[0,14,2.4],[76,14,2.4],[93,12.3,2.55],[104,7.2,2.8],[110,2.2,2.95]],
  'barge-fallback': [[-108,11.4,2.3],[-95,13.3,2.4],[75,13.3,2.4],[92,11.875,2.55],[103,7.125,2.75],[108,2.09,2.95]],
  'tug-fallback': [[-9,2.6125,1.35],[-6.5,3.23,1.4],[-2,3.3725,1.5],[2,3.2775,1.75],[5.5,2.66,2.15],[7.8,1.52,2.5],[9,.285,2.85]],
  // Matches the Blender tug's continuous deck loft, in metres and +Z forward.
  tug: [[-9,2.6125,1.35],[-6.5,3.23,1.4],[-2,3.3725,1.5],[2,3.2775,1.75],[5.5,2.66,2.15],[7.8,1.52,2.5],[9,.285,2.85]],
}

/** Player, cargo barge, and the two nearest traffic boats. */
export class HullWaterMasks {
  private readonly slots = Array.from({length:4}, () => ({
    inverse: uniform(new Matrix4()),
    count: uniform(0, 'int'),
    stations: uniformArray(Array.from({length:MAX_STATIONS}, () => new Vector4())),
    profile: null as HullWaterProfile | null,
  }))

  /** Called after every body's visual transform has been synchronized. */
  update(sources: readonly HullWaterSource[]): void {
    if (sources.length>this.slots.length) throw new Error('Too many water exclusion hulls')
    this.slots.forEach((slot,index) => {
      const source=sources[index]
      if (!source) { slot.count.value=0; return }
      const profile=source.profile
      if (profile.length<2 || profile.length>MAX_STATIONS) throw new Error('Invalid hull water profile')
      source.object.updateWorldMatrix(true,false)
      slot.inverse.value.copy(source.object.matrixWorld).invert()
      slot.count.value=profile.length
      if (slot.profile===profile) return
      slot.profile=profile
      profile.forEach(([z,w,y],i) => (slot.stations.array[i] as Vector4).set(z,w,y,0))
    })
  }

  /** Run after shading derivatives; discard before writing color or depth. */
  readonly clip = Fn(([worldPosition]: [TSLNode]) => {
    const dry=float(0).toVar()
    for (const slot of this.slots) {
      const p=slot.inverse.mul(vec4(worldPosition,1)).xyz.toVar()
      const eye=slot.inverse.mul(vec4(cameraPosition,1)).xyz.toVar()
      const ray=p.sub(eye).toVar()
      If(slot.count.greaterThan(1), () => {
        Loop({start:0, end:slot.count.sub(1), type:'int'}, ({i}) => {
          const a: TSLNode=slot.stations.element(i)
          const next: TSLNode=slot.stations.element(i.add(1))
          // Each pair is a sloped deck strip. Intersect the eye-to-water ray
          // with that strip, rather than cutting a vertical hole above it.
          // Thus every discarded pixel is backed by the visible opaque deck.
          const slope=next.z.sub(a.z).div(next.x.sub(a.x))
          const denominator=ray.y.sub(slope.mul(ray.z))
          If(denominator.lessThan(-0.0001), () => {
            const deckAtEye=a.z.sub(DECK_OVERLAP).add(slope.mul(eye.z.sub(a.x)))
            const distance=deckAtEye.sub(eye.y).div(denominator)
            const hit=eye.add(ray.mul(distance))
            const t=hit.z.sub(a.x).div(next.x.sub(a.x)).clamp(0,1)
            const width=mix(a.y,next.y,t)
            If(distance.greaterThanEqual(1).and(hit.z.greaterThanEqual(a.x)).and(hit.z.lessThanEqual(next.x))
              .and(hit.x.abs().lessThan(width)), () => { dry.assign(1) })
          })
        })
      })
    }
    Discard(dry.greaterThan(0))
  }, 'void')
}
